export interface Position {
  x: number;
  y: number;
}

// Recent files metadata interface for landing page
export interface RecentFileMetadata {
  id: string;
  name: string;
  lastOpened?: number;
  lastSaved: number;
  nodeCount?: number;
  thumbnail?: string;
  isLocal?: boolean;
  isFavorite?: boolean;
  ownerId?: string;
  isShared?: boolean;
  sharedPermission?: 'view' | 'edit';
  sharedBy?: string;
  createdAt?: number;
  /** Project/folder the workspace is filed under; null or undefined = unfiled. */
  folderId?: string | null;
}

// Cloud workspace metadata interface
export interface ProjectMetadata {
  id: string;
  name: string;
  lastSaved: number;
  thumbnail?: string;
}

export interface StoryboardBox {
  id: string;
  prompt: string;
  imageUrl?: string;
  status: 'idle' | 'generating' | 'completed' | 'error';
  errorMessage?: string;
}

/** What a Reference node shows, for sorting references in lists. */
export type ReferenceTag = 'character' | 'prop' | 'background' | 'style' | 'other';

/** A Reference node an Image or Video node uses without a connection, chosen from a list. */
export interface PickedReference {
  id: string;
  kind: 'image' | 'video' | 'audio';
  /** The Reference node: its current media and name are used, like a connection's. */
  nodeId?: string;
  /**
   * Set when the Reference node lives in another workspace of the same project: it is used
   * from there, not copied. `url`, `label` and `tag` then hold what it had when picked, for
   * until that workspace has been read.
   */
  workspaceId?: string;
  /** Media picked by address, from before picks named a Reference node; or the snapshot above. */
  url?: string;
  label: string;
  tag?: ReferenceTag;
  mimeType?: string;
  durationSec?: number;
  sizeBytes?: number;
}

export interface NodeData {
  title?: string;
  imageUrl?: string;
  referenceImages?: string[]; // Supports multiple references for Character/Entity nodes
  
  // Image natural dimensions
  originalWidth?: number;
  originalHeight?: number;
  // Audio reference node (a file:// reference once saved, a data URL before that)
  audioUrl?: string;
  audioFileName?: string;
  audioMimeType?: string;
  audioSizeBytes?: number;
  audioDurationSec?: number;
  // Reference node: which kind of media it holds (image fields, audio fields or videoUri)
  referenceKind?: 'image' | 'audio' | 'video';
  // Reference node: the file name its media was saved under in the local auto-save folder; the workspace save uploads under the same name
  localCopyName?: string;
  // Reference node: the preview is hidden and the node folded to one row; the height to return to when it opens again
  referenceCompact?: boolean;
  referenceExpandedHeight?: number;
  // Reference node: its tag, and whether its title is a name the user gave (kept when the media changes)
  referenceTag?: ReferenceTag;
  referenceNamed?: boolean;
  // Image and Video nodes: workspace media chosen as references without a connection, used after the connected ones
  pickedReferences?: PickedReference[];
  /** Reactions on the node, as on Ideas cards: emoji → the ids of the people who gave it (utils/nodeReactions.ts) */
  reactions?: Record<string, string[] | undefined>;
  videoFileName?: string;
  videoMimeType?: string;
  videoSizeBytes?: number;
  videoDurationSec?: number;
  // Quick Editor node: the timeline project (assets, tracks and clips; media bytes live in the local media store)
  quickEditor?: import('./utils/quickEditor').QuickEditorProject;
  // Video specific
  prompt?: string;
  videoUri?: string;
  lastVeoVideoRef?: any; // Serialized Veo video reference for extend-video
  videoConfig?: {
    model: string;
    aspectRatio: string;
    resolution: string;
    duration?: string;
    generationMode?: 'text-to-video' | 'image-to-video' | 'frames-to-video' | 'ingredients-to-video' | 'extend-video' | 'reference-to-video';
    negativePrompt?: string;
    // Venice / Seedance specific
    audio?: boolean;          // generate audio (Seedance default true)
    audioRole?: 'music' | 'reference'; // how connected Audio nodes are used: background music (audio_url) or voice/SFX donors (reference_audio_urls)
    veniceModelId?: string;   // exact Venice model id used for the queued job (needed to poll /video/retrieve)
    veniceDownloadUrl?: string; // pre-signed URL for VPS-backed models
    generationOwnerId?: string; // user who queued the job; only their client resumes it after a reload
    // Reference clips (Seedance reference-to-video): the part of each connected video that is sent, by source node id
    referenceClips?: Record<string, { startSec: number; endSec: number }>;
    extendFromClip?: boolean;   // Seedance 2.5: ask for a continuation of the clip rather than a restyle
    matchClipAspect?: boolean;  // send aspect_ratio "adaptive" so the output matches the clip (default on)
  };
  // Image Generation specific
  imageGenConfig?: {
    model: string;
    aspectRatio: string;
    numberOfImages?: number;
    // OpenAI specific
    size?: string;
    quality?: string;
    // Venice resolution-tier models (Nano Banana Pro, GPT Image 2): "1K" | "2K" | "4K"
    resolution?: string;
    style?: string;
    // ComfyUI specific
    steps?: number;
    customWorkflowId?: string;
  };
  // Sketch specific
  sketchConfig?: {
    aspectRatio: string;
    brushColor: string;
    brushSize: number;
    backgroundColor: string;
  };
  // Script Processing specific
  scriptConfig?: {
    model: string;
    fileName?: string;
    fileData?: string; // Base64 PDF
    mimeType?: string;
    currentPage?: number;
    totalPages?: number;
  };
  // Text Prompt specific
  textPromptData?: {
    sceneNumber: string;
    cameraAngle: string;
    visualDescription: string;
  };
  // Write Node specific
  writeData?: {
    draft: string;
    refined?: string;
    modelId?: string;
  };
  // Entity specific (Char, Prop, Location/Set)
  entityData?: {
    name: string;
    description: string;
    setType?: 'Interior' | 'Exterior';
    model?: string;
    // visual reference is stored in root imageUrl property for consistency with other image nodes
  };
  // Storyboard specific
  storyboardConfig?: {
    model: string;
    aspectRatio: string;
    columns: number;  // Default 6
    rows: number;     // Default 3
    boxes: StoryboardBox[];
  };
  // Share node specific
  shareData?: {
    caption?: string;
    platform: 'twitter';
    postStatus?: 'idle' | 'posting' | 'success' | 'error' | 'scheduled';
    postError?: string;
    tweetId?: string;
    tweetUrl?: string;
    lastPostedAt?: number;
    isLocked?: boolean;
    // Scheduling fields
    scheduledPostId?: string; // Firestore id of the scheduled post
    scheduledAt?: number; // Unix timestamp when post should be published
    scheduledStatus?: 'pending' | 'posted' | 'failed' | 'cancelled';
  };

  generatedImageUri?: string;
  
  generationStatus?: 'idle' | 'generating' | 'completed' | 'error';
  errorMessage?: string;
  timestamp?: number;
  cost?: number;
  
  // Job Recovery for long running tasks (Sora)
  jobId?: string;
  progress?: number;
  
  [key: string]: any;
}

export enum NodeType {
  IMAGE_VIEWER = 'IMAGE_VIEWER',
  VIDEO_GENERATOR = 'VIDEO_GENERATOR',
  IMAGE_GENERATOR = 'IMAGE_GENERATOR',
  SKETCH_PAD = 'SKETCH_PAD',
  SCRIPT_PROCESSOR = 'SCRIPT_PROCESSOR',
  TEXT_PROMPT = 'TEXT_PROMPT',
  WRITE_NODE = 'WRITE_NODE',
  CHARACTER_NODE = 'CHARACTER_NODE',
  PROP_NODE = 'PROP_NODE',
  LOCATION_NODE = 'LOCATION_NODE',
  STORAGE_BROWSER = 'STORAGE_BROWSER',
  STORYBOARD_NODE = 'STORYBOARD_NODE',
  SHARE_NODE = 'SHARE_NODE',
  AUDIO_REFERENCE = 'AUDIO_REFERENCE', // legacy: rendered by the Reference node
  MEDIA_REFERENCE = 'MEDIA_REFERENCE', // Reference node: one image, audio file or video
  QUICK_EDITOR = 'QUICK_EDITOR',       // Timeline editor. The workspace's Edit page keeps its edit in one node of this type that is not drawn on the canvas
}

export interface CanvasNode {
  id: string;
  type: NodeType;
  position: Position;
  // Container dimensions
  size?: {
    width: number;
    height: number;
  };
  data: NodeData;
}

export interface HistoryItem {
  id: string;
  type: NodeType;
  data: NodeData;
  timestamp: number;
}

/**
 * Lightweight history item for efficient storage
 * Only stores metadata + file references (no base64 data)
 */
export interface LightHistoryItem {
  id: string;
  type: NodeType;
  timestamp: number;
  // Only metadata - no large image/video data
  title?: string;
  prompt?: string;
  cost?: number;
  // References to files (small strings like "file://abc123")
  imageRef?: string;
  videoRef?: string;
}

export interface ViewportTransform {
  x: number;
  y: number;
  scale: number;
}

export interface Connection {
  id: string;
  sourceId: string;
  targetId: string;
}

export type UserRole = 'user' | 'admin' | 'superadmin';

// ==================== MULTI-TIER USER HIERARCHY ====================

/**
 * Account type determines the user's tier in the platform hierarchy.
 * - individual: self-signup single-license owner (default for all existing users)
 * - collaborator: working with other users via team invites
 * - team_owner: self-signup account that created a team (≤10 members)
 * - enterprise_owner: owner of an enterprise account (upgraded from team or direct)
 * - company: internal app-owner staff (Noodle Studio)
 */
export type AccountType = 'individual' | 'collaborator' | 'team_owner' | 'enterprise_owner' | 'company';

/**
 * Granular role within the hierarchy. Maps to access profiles.
 *
 * Customer tier:
 *   individual         – single-license self-signup
 *   collaborator       – invited team member
 *   team_owner         – created a team (≤10), has admin rights over the team
 *
 * Enterprise tier:
 *   enterprise_owner   – owns the enterprise org, highest within org
 *   enterprise_admin   – delegated admin within the org
 *   enterprise_team_lead – leads a team group within the org
 *   enterprise_member  – regular member within the org
 *   enterprise_viewer  – read-only access within the org
 *   enterprise_guest   – time-limited external access
 *
 * Company tier (app owners):
 *   company_super_admin – highest platform access
 *   company_admin       – scoped platform admin via access profile
 */
export type HierarchyRole =
  // Customer
  | 'individual'
  | 'collaborator'
  | 'team_owner'
  // Enterprise
  | 'enterprise_owner'
  | 'enterprise_admin'
  | 'enterprise_team_lead'
  | 'enterprise_member'
  | 'enterprise_viewer'
  | 'enterprise_guest'
  // Company (app owners)
  | 'company_super_admin'
  | 'company_admin';

/**
 * Hierarchy level for quick ordering comparisons.
 * Higher number = more authority.
 */
export const HIERARCHY_LEVEL: Record<HierarchyRole, number> = {
  // Company (highest)
  company_super_admin: 100,
  company_admin: 90,
  // Enterprise
  enterprise_owner: 70,
  enterprise_admin: 60,
  enterprise_team_lead: 50,
  enterprise_member: 40,
  enterprise_viewer: 30,
  enterprise_guest: 20,
  // Customer
  team_owner: 45,
  collaborator: 35,
  individual: 25,
};

export interface UserProfile {
  name: string;
  email: string;
  picture: string;
  sub: string;
  role?: UserRole;
  permissions?: string[];
  subscription?: UserSubscription;
  verificationLink?: string;
  /** Solana wallet address when signed in via wallet (Sign-In With Solana) */
  walletAddress?: string;
  // Multi-tier hierarchy fields (all optional for backward compatibility)
  accountType?: AccountType;
  hierarchyRole?: HierarchyRole;
  activeOrgId?: string;
  orgMemberships?: string[];   // array of orgIds for quick lookup
}

export type SubscriptionTier = 'free' | 'pro' | 'enterprise';
export type SubscriptionStatus = 'active' | 'cancelled' | 'past_due' | 'expired' | 'on_trial';

export interface UserSubscription {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  // Legacy LemonSqueezy fields (deprecated)
  lemonSqueezyCustomerId?: string;
  lemonSqueezySubscriptionId?: string;
  variantId?: string;
  productId?: string;
  currentPeriodStart?: number;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
  updatedAt?: number;
  // Solana USDC Credit System
  credits?: number; // Number of credits available (1 credit = 1 month of pro access)
  lastCreditUsedAt?: number; // Timestamp when last credit was consumed
  currentCreditExpiresAt?: number; // When current month's credit expires
  solanaWalletAddress?: string; // User's connected Solana wallet
}

// Credit purchase transaction record
export interface CreditTransaction {
  id: string;
  userId: string;
  signature: string; // Solana transaction signature
  creditAmount: number; // Number of credits purchased
  usdcAmount: number; // USDC paid
  senderWallet: string; // Buyer's wallet address
  status: 'pending' | 'verified' | 'failed';
  createdAt: number;
  verifiedAt?: number;
  error?: string;
}

// Feature limits per subscription tier
export const SUBSCRIPTION_LIMITS: Record<SubscriptionTier, {
  maxWorkspaces: number;
  maxNodesPerWorkspace: number;
  maxStorageMB: number;
  aiGenerationsPerDay: number;
  videoGenerationEnabled: boolean;
  exportWatermark: boolean;
  cloudSyncEnabled: boolean;
}> = {
  free: {
    maxWorkspaces: 1, // 1 local workspace only
    maxNodesPerWorkspace: 3, // Limited to 3 nodes
    maxStorageMB: 0, // No cloud storage
    aiGenerationsPerDay: -1, // unlimited - users use their own API keys
    videoGenerationEnabled: true, // enabled - users use their own API keys
    exportWatermark: false,
    cloudSyncEnabled: true, // Enabled for all tiers - concurrent access across devices
    // TODO: In future, gate this behind an "Active Sync" toggle in subscription settings
  },
  pro: {
    maxWorkspaces: -1, // unlimited
    maxNodesPerWorkspace: -1, // unlimited
    maxStorageMB: 1024, // 1GB
    aiGenerationsPerDay: -1,
    videoGenerationEnabled: true,
    exportWatermark: false,
    cloudSyncEnabled: true,
  },
  enterprise: {
    maxWorkspaces: -1,
    maxNodesPerWorkspace: -1,
    maxStorageMB: 102400, // 100GB
    aiGenerationsPerDay: -1,
    videoGenerationEnabled: true,
    exportWatermark: false,
    cloudSyncEnabled: true,
  },
};

export interface LocalUserCredentials {
  email: string;
  passwordHash: string;
  name: string;
  picture?: string;
}

// --- CONCURRENT ACCESS / SESSION MANAGEMENT ---

/**
 * Maximum concurrent device sessions per account.
 * TODO: Make configurable per subscription tier in future
 * (e.g., free: 3, pro: 10, enterprise: unlimited)
 */
export const SESSION_LIMIT = 5;

/**
 * Represents an active device/browser session accessing the account.
 * Stored in Firestore at users/{uid}/sessions/{sessionId}
 */
export interface DeviceSession {
  sessionId: string;
  userId: string;
  deviceName: string;      // e.g. "Chrome on macOS"
  browser: string;         // e.g. "Chrome 120"
  os: string;              // e.g. "macOS 14.2"
  deviceType: 'desktop' | 'mobile' | 'tablet';
  createdAt: number;       // Timestamp when session was created
  lastActiveAt: number;    // Timestamp of last heartbeat
  isActive: boolean;       // Whether this session is currently active
  tabId: string;           // Unique tab identifier (from sessionStorage)
  isLeaderTab: boolean;    // Whether this tab is the BroadcastChannel leader for its browser
}

/**
 * Schema version for project documents.
 * v1: All nodes embedded in single project document
 * v2: Nodes stored as sub-documents in projects/{id}/nodes/{nodeId}
 */
export type ProjectSchemaVersion = 1 | 2;

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

export interface ApiKeys {
  googleGenAi?: string;
  openAi?: string;
  venice?: string;
  vertexApiKey?: string;
  vertexProjectId?: string;
  vertexLocation?: string;
}

// Storage categories for file organization
export type StorageCategory =
  | 'Generated_Images'
  | 'Uploaded_Images'
  | 'Videos'
  | 'Audio'
  | 'Sketches'
  | 'Characters'
  | 'Sets'
  | 'Scripts'
  | 'Other';

// File metadata stored in Firestore for stable references
export interface FileMetadata {
  id: string;              // UUID - never changes
  displayName: string;     // User-editable name
  storagePath: string;     // Current Firebase Storage path
  downloadUrl: string;     // Current download URL
  type: 'image' | 'video' | 'audio' | 'file';
  contentType: string;     // MIME type
  size: number;            // File size in bytes
  category: StorageCategory;
  createdAt: number;
  updatedAt: number;
  nodeId?: string;         // Optional: which node created this file
  workspaceName?: string;  // Optional: workspace this file belongs to
}

export const GRID_SIZE = 20;

// Estimated Pricing per generation (USD)
export const MODEL_PRICING: Record<string, number> = {
  // Image Models (Google)
  'gemini-2.5-flash-image': 0.003, // ~$0.003 per image
  'gemini-3-pro-image-preview': 0.04, // ~$0.04 per image
  
  // Image Models (OpenAI)
  'dall-e-3': 0.040, // Standard 1024x1024. HD is 0.080, different sizes vary slightly.
  'dall-e-2': 0.020, // 1024x1024

  // Video Models (Veo estimates based on duration/compute)
  'veo-3.1-fast-generate-preview': 0.20, 
  'veo-3.1-generate-preview': 0.40,
  
  // OpenAI Video Models (Estimates)
  'sora-2': 0.25, 
  'sora-2-pro': 0.50,

  // Text Models (Per 1M tokens approx, averaged for a script breakdown)
  'gemini-2.5-flash': 0.01, 
  'gemini-3-pro-preview': 0.05
};

// ==================== ADMIN PANEL TYPES ====================

export interface AdminUser {
  uid: string;
  email: string;
  name: string;
  role: 'admin' | 'superadmin';
  permissions: string[];
  createdAt: number;
  createdBy: string;
  lastActiveAt?: number;
  isActive: boolean;
}

export const ADMIN_PERMISSIONS = [
  'manage_users',
  'manage_subscriptions',
  'manage_features',
  'manage_config',
  'view_analytics',
  'view_audit_logs',
] as const;

export type AdminPermission = typeof ADMIN_PERMISSIONS[number];

// Subscription plan that admins can configure
export interface SubscriptionPlanConfig {
  id: string;
  tier: SubscriptionTier;
  displayName: string;
  description: string;
  price: number;
  currency: string;
  billingPeriod: 'monthly' | 'yearly' | 'one-time';
  features: {
    maxWorkspaces: number;       // -1 = unlimited
    maxNodesPerWorkspace: number; // -1 = unlimited
    maxStorageMB: number;
    aiGenerationsPerDay: number;  // -1 = unlimited
    cloudSyncEnabled: boolean;
    videoGenerationEnabled: boolean;
    exportWatermark: boolean;
  };
  isActive: boolean;
  sortOrder: number;
  badge?: string;  // e.g. 'Popular', 'Best Value'
  createdAt: number;
  updatedAt: number;
  updatedBy: string;
}

export interface FeatureToggle {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  targetTiers: SubscriptionTier[];
  updatedAt: number;
  updatedBy: string;
}

export type AuditLogCategory = 'subscription' | 'feature' | 'config' | 'user' | 'auth' | 'system' | 'api' | 'storage' | 'ai' | 'social';
export type AuditLogSeverity = 'info' | 'warning' | 'error' | 'critical';
export type ActorType = 'admin' | 'user' | 'system';

export interface AdminAuditLog {
  id: string;
  // Actor info
  actorType: ActorType;
  adminUid?: string;
  adminEmail?: string;
  userId?: string;
  userEmail?: string;
  action: string;
  category: AuditLogCategory;
  severity: AuditLogSeverity;
  details?: string;
  oldValue?: any;
  newValue?: any;
  timestamp: number;
  // Enhanced fields for troubleshooting
  ipAddress?: string;
  userAgent?: string;
  targetUserId?: string;        // User affected by this action
  targetResourceType?: string;  // 'plan' | 'user' | 'feature' | 'workspace' | etc.
  targetResourceId?: string;    // ID of the affected resource
  success: boolean;
  errorCode?: string;
  errorMessage?: string;
  stackTrace?: string;
  requestId?: string;           // For correlating related logs
  duration?: number;            // Operation duration in ms
}

export interface AuditLogFilters {
  search?: string;
  actorTypes?: ActorType[];
  adminUid?: string;
  userId?: string;
  targetUserId?: string;
  categories?: AuditLogCategory[];
  severities?: AuditLogSeverity[];
  actions?: string[];
  success?: boolean | 'all';
  dateRange: { start: number; end: number };
  preset: AdminAnalyticsPreset;
}

export interface AuditLogListResponse {
  logs: AdminAuditLog[];
  total: number;
  hasMore: boolean;
  cursor?: string;
}

// ==================== ADMIN ANALYTICS TYPES ====================

export interface TimeSeriesPoint {
  date: string;   // ISO date string or label e.g. '2026-02-01', 'Week 5'
  value: number;
  label?: string; // Optional display label
}

export interface TierBreakdown {
  tier: string;
  count: number;
  percentage: number;
}

export interface StatusBreakdown {
  status: string;
  count: number;
  percentage: number;
}

export interface NodeTypeBreakdown {
  nodeType: string;
  count: number;
  percentage: number;
}

export interface ModelUsageEntry {
  model: string;
  count: number;
  estimatedCost: number;
}

export interface MediaCategoryEntry {
  category: string;
  count: number;
  sizeBytes: number;
}

export interface AdminAnalyticsFilters {
  dateRange: { start: number; end: number };
  preset: AdminAnalyticsPreset;
  tier?: SubscriptionTier | 'all';
  granularity: 'daily' | 'weekly' | 'monthly';
}

export type AdminAnalyticsPreset = '7d' | '30d' | '90d' | '1y' | 'all' | 'custom';

export interface KpiCard {
  label: string;
  value: number;
  formattedValue: string;
  delta: number;       // percentage change vs prior period, e.g. 12.5 = +12.5%
  trend: TimeSeriesPoint[];  // sparkline data
}

export interface AdminAnalyticsData {
  // Section A: KPI Overview
  kpis: {
    totalUsers: KpiCard;
    activeUsers: KpiCard;
    revenue: KpiCard;
    totalProjects: KpiCard;
    storageUsed: KpiCard;
    aiGenerations: KpiCard;
  };
  // Section B: Users & Revenue
  userGrowth: TimeSeriesPoint[];
  cumulativeUsers: TimeSeriesPoint[];
  subscriptionBreakdown: TierBreakdown[];
  subscriptionStatusBreakdown: StatusBreakdown[];
  revenueTrend: (TimeSeriesPoint & { cumulative: number; count: number })[];
  // Section C: Content & AI
  nodeTypeBreakdown: NodeTypeBreakdown[];
  modelUsage: ModelUsageEntry[];
  mediaByCategory: MediaCategoryEntry[];
  // Section D: Social
  socialPostsByStatus: StatusBreakdown[];
  socialPostsTrend: (TimeSeriesPoint & { posted: number; failed: number; pending: number })[];
}

// ==================== GRANULAR PERMISSION SYSTEM ====================

/**
 * Granular permissions across feature categories.
 * Access profiles bundle these into named sets assigned to roles.
 */
export const PERMISSIONS = {
  // Workspace permissions
  WORKSPACE_CREATE: 'workspace.create',
  WORKSPACE_EDIT: 'workspace.edit',
  WORKSPACE_DELETE: 'workspace.delete',
  WORKSPACE_SHARE: 'workspace.share',
  WORKSPACE_VIEW: 'workspace.view',
  WORKSPACE_TRANSFER: 'workspace.transfer',

  // Team permissions
  TEAM_CREATE: 'team.create',
  TEAM_INVITE: 'team.invite',
  TEAM_REMOVE: 'team.remove',
  TEAM_EDIT_ROLES: 'team.editRoles',

  // Billing permissions
  BILLING_VIEW: 'billing.view',
  BILLING_MANAGE: 'billing.manage',
  BILLING_UPGRADE: 'billing.upgrade',

  // Analytics permissions
  ANALYTICS_VIEW_OWN: 'analytics.viewOwn',
  ANALYTICS_VIEW_TEAM: 'analytics.viewTeam',
  ANALYTICS_VIEW_ORG: 'analytics.viewOrg',
  ANALYTICS_VIEW_PLATFORM: 'analytics.viewPlatform',
  ANALYTICS_EXPORT: 'analytics.export',

  // Admin permissions (platform-level)
  ADMIN_MANAGE_USERS: 'admin.manageUsers',
  ADMIN_MANAGE_SUBSCRIPTIONS: 'admin.manageSubscriptions',
  ADMIN_MANAGE_FEATURES: 'admin.manageFeatures',
  ADMIN_MANAGE_CONFIG: 'admin.manageConfig',
  ADMIN_VIEW_AUDIT_LOGS: 'admin.viewAuditLogs',

  // Content permissions
  CONTENT_GENERATE: 'content.generate',
  CONTENT_UPLOAD: 'content.upload',
  CONTENT_DOWNLOAD: 'content.download',
  CONTENT_DELETE: 'content.delete',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Named bundle of permissions, assignable to roles.
 * System profiles are seeded; custom profiles can be created per-org.
 */
export interface AccessProfile {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
  /** 'platform' profiles apply globally; 'org' profiles are scoped to an organization */
  scope: 'platform' | 'org';
  orgId?: string;               // set when scope === 'org'
  isDefault: boolean;           // true for seeded system profiles
  isSystem: boolean;            // true = cannot be deleted
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

/**
 * Maps a hierarchy role to an access profile.
 * Platform-level mappings are the defaults; org-level mappings override for that org.
 */
export interface RoleAccessMapping {
  id: string;
  role: HierarchyRole;
  accessProfileId: string;
  orgId?: string;               // null = platform default
  createdBy: string;
  createdAt: number;
}

// ==================== ORGANIZATION & TEAM ====================

export type OrganizationType = 'team' | 'enterprise';
export type OrgMemberStatus = 'active' | 'suspended' | 'invited' | 'deactivated';

/**
 * Top-level tenant entity. Created when a user upgrades to team or enterprise.
 */
export interface Organization {
  id: string;
  name: string;
  slug: string;                 // URL-friendly unique identifier
  ownerId: string;              // Firebase UID of the org creator
  type: OrganizationType;
  maxMembers: number;           // 10 for team, configurable for enterprise
  billingEntityId?: string;     // links to BillingEntity for org-level billing
  settings: OrgSettings;
  createdAt: number;
  updatedAt: number;
}

export interface OrgSettings {
  defaultWorkspaceVisibility: WorkspaceVisibility;
  allowGuestAccess: boolean;
  guestAccessMaxDays: number;   // max days for guest invitation expiry
  requireApprovalForInvites: boolean;
  customBranding?: {
    logoUrl?: string;
    primaryColor?: string;
    orgDisplayName?: string;
  };
}

/**
 * Sub-group within an organization.
 * Team accounts have one implicit group; enterprise accounts can have many.
 */
export interface TeamGroup {
  id: string;
  orgId: string;
  name: string;
  description?: string;
  leadUserId?: string;          // team lead
  memberIds?: string[];         // user IDs in this team group
  createdBy?: string;           // UID of creator
  createdAt: number;
  updatedAt: number;
}

/**
 * Bridge between a user and an organization.
 * A user can belong to multiple orgs (e.g., freelancer in 2 enterprise orgs).
 */
export interface OrgMembership {
  id: string;
  orgId: string;
  userId: string;               // Firebase UID
  userEmail: string;            // denormalized for display
  userName?: string;            // denormalized for display
  teamGroupIds: string[];       // which team groups within the org
  role: HierarchyRole;
  accessProfileId?: string;     // override profile for this member (null = use role default)
  invitedBy: string;            // UID of inviter
  joinedAt: number;
  status: OrgMemberStatus;
  lastActiveAt?: number;
}

/**
 * Invitation to join an organization.
 */
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface Invitation {
  id: string;
  orgId: string;
  orgName?: string;             // denormalized for display in invitation UI
  email: string;                // invitee email
  role: HierarchyRole;
  teamGroupIds: string[];
  accessProfileId?: string;
  invitedBy: string;            // UID
  inviterName?: string;         // denormalized
  token: string;                // unique, URL-safe token for accept link
  message?: string;             // optional personal message
  expiresAt: number;
  status: InvitationStatus;
  acceptedAt?: number;
  acceptedByUserId?: string;
  createdAt: number;
}

// ==================== WORKSPACE ACCESS & SHARING ====================

export type WorkspacePermission = 'viewer' | 'editor' | 'admin';
export type WorkspaceVisibility = 'private' | 'team' | 'org' | 'public';

/**
 * Per-workspace access grant for a user or team group.
 */
export interface WorkspaceAccess {
  granteeId: string;            // userId or teamGroupId
  granteeType: 'user' | 'team_group';
  permission: WorkspacePermission;
  grantedBy: string;            // UID of granter
  grantedAt: number;
  expiresAt?: number;           // for time-limited guest access
}

/**
 * Extended project metadata fields for workspace isolation & sharing.
 * These are added alongside existing project fields (ownerId, name, nodes, etc.).
 */
export interface ProjectAccessFields {
  orgId?: string;
  teamGroupId?: string;
  sharedWith: WorkspaceAccess[];
  visibility: WorkspaceVisibility;
  // Flattened arrays for Firestore rule evaluation (rules can't iterate objects)
  sharedUserIds: string[];
  sharedTeamGroupIds: string[];
}

// ==================== NOTIFICATION SYSTEM ====================

export type NotificationType =
  | 'invitation_received'
  | 'invitation_accepted'
  | 'workspace_shared'
  | 'role_changed'
  | 'member_joined'
  | 'member_removed'
  | 'team_added'
  | 'team_removed'
  | 'subscription_expiring'
  | 'subscription_changed'
  | 'system_announcement'
  // Tasks of a workspace's ideas (utils/ideas.ts IdeaNoticeKind)
  | 'task_assigned'
  | 'task_done'
  // A library project someone wants to share: answered with Accept or Decline (netlify/functions/api/project-sharing.ts)
  | 'project_invitation'
  // A copy of a whole project sent to this person, and (to the sender) one accepted (netlify/functions/api/project-copies.ts)
  | 'project_copy'
  | 'project_copy_done';

export interface AppNotification {
  id: string;
  userId: string;               // recipient
  type: NotificationType;
  title: string;
  body: string;
  data?: Record<string, any>;   // type-specific payload (orgId, projectId, etc.)
  read: boolean;
  actionUrl?: string;           // deep link for click handling
  createdAt: number;
}

// ==================== BILLING FRAMEWORK ====================

/**
 * Supported payment providers. 'solana' is current; others are future stubs.
 */
export type PaymentProviderType = 'solana' | 'stripe' | 'lemonsqueezy' | 'manual_invoice';

export type BillingEntityType = 'individual' | 'organization';

export type BillingCycle = 'monthly' | 'yearly' | 'one-time' | 'credits';

/**
 * A billable entity — either an individual user or an organization.
 * Abstracts who is paying from how they're paying.
 */
export interface BillingEntity {
  id: string;
  type: BillingEntityType;
  entityId: string;             // userId (for individual) or orgId (for organization)
  paymentProviders: PaymentProviderConfig[];
  activeSubscriptionId?: string;
  createdAt: number;
  updatedAt: number;
}

export interface PaymentProviderConfig {
  provider: PaymentProviderType;
  isActive: boolean;
  providerCustomerId?: string;  // e.g. Stripe customer ID, Solana wallet address
  metadata?: Record<string, any>;
}

/**
 * A subscription record — replaces the tightly-coupled UserSubscription for new flows.
 * Legacy UserSubscription on Firestore is preserved for backward compat.
 */
export interface BillingSubscription {
  id: string;
  billingEntityId: string;
  planId: string;               // references SubscriptionPlanConfig.id
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  paymentProvider: PaymentProviderType;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  seatCount?: number;           // for org billing (number of paid seats)
  seatPrice?: number;           // price per seat per billing cycle
  totalAmount: number;          // total charge per billing cycle
  currency: string;
  billingCycle: BillingCycle;
  createdAt: number;
  updatedAt: number;
}

/**
 * A payment transaction — provider-agnostic record.
 */
export interface PaymentTransaction {
  id: string;
  billingEntityId: string;
  subscriptionId?: string;
  paymentProvider: PaymentProviderType;
  providerTransactionId: string; // Solana signature, Stripe payment_intent ID, etc.
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  description?: string;
  metadata?: Record<string, any>;
  createdAt: number;
  completedAt?: number;
}

// ==================== ANALYTICS SCOPING ====================

/**
 * Analytics scope determines what data a viewer can see.
 */
export type AnalyticsScope = 'platform' | 'org' | 'team' | 'user';

export interface ScopedAnalyticsRequest {
  scope: AnalyticsScope;
  scopeId: string;              // orgId, teamGroupId, or userId depending on scope
  filters: AdminAnalyticsFilters;
}

// ==================== WORKSPACE SHARING ====================

export type SharePermission = 'view' | 'edit';

/**
 * Per-user sharing metadata stored in the project document's `sharedWith` map.
 * Key is the Firebase UID of the shared user.
 */
export interface SharedUserInfo {
  permission: SharePermission;
  email: string;
  name: string;
  sharedAt: number;
  sharedBy: string;
}

/**
 * Invitation for a non-platform user to access a shared workspace.
 * Stored in Firestore `invitations/{invitationId}`.
 */
export type WorkspaceInvitationStatus = 'pending' | 'accepted' | 'expired';

export interface WorkspaceInvitation {
  id: string;
  email: string;
  projectId: string;
  projectName: string;
  permission: SharePermission;
  invitedBy: string;
  inviterName: string;
  inviterEmail: string;
  status: WorkspaceInvitationStatus;
  createdAt: number;
  expiresAt: number;
  acceptedAt?: number;
  acceptedByUid?: string;
}

/**
 * Changelog entry for tracking workspace edits.
 * Stored in Firestore `projects/{projectId}/changelog/{logId}`.
 */
export type ChangelogAction =
  | 'node_added'
  | 'node_updated'
  | 'node_deleted'
  | 'connection_added'
  | 'connection_deleted'
  | 'project_renamed'
  | 'user_shared'
  | 'user_unshared'
  | 'permission_changed';

export interface ChangelogEntry {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  action: ChangelogAction;
  nodeId?: string;
  nodeType?: string;
  details?: string;
  timestamp: number;
}

// ==================== REAL-TIME COLLABORATION PRESENCE ====================

/**
 * Presence data for a user in a workspace.
 * Stored in Firebase Realtime Database at /presence/{projectId}/{userId}
 */
export interface UserPresence {
  userId: string;
  displayName: string;
  photoURL?: string;
  color: string;                // assigned avatar border color
  cursorX?: number;
  cursorY?: number;
  selectedNodeId?: string;
  lastSeen: number;
  isActive: boolean;
}

/**
 * Soft lock on a node being actively edited.
 * Prevents concurrent edits to the same node without full CRDT.
 */
export interface NodeEditLock {
  nodeId: string;
  lockedBy: string;             // userId
  lockedByName: string;
  lockedAt: number;
  expiresAt: number;            // auto-release after 30s of inactivity
}